import { Col, Empty, Image, message, Row, Space, Spin, Table, Tag } from "antd";
import { FileExcelOutlined, FilePdfOutlined } from "@ant-design/icons";
import axios from "axios";
import moment from "moment";
import React, { Component } from "react";
import { connect } from "react-redux";
import { ResponsiveBar } from "@nivo/bar";
import { ButtonItem, ErrorItem, Form, TableItemDinamico } from "../../../../Components/Items";
import FiltrosCalidad from "./FiltrosCalidad";
import {
  categoriaEvidencia,
  descargarPdfCalidad,
  exportarExcel,
  fmtDuracion,
  fmtEjecutado,
  fmtFecha,
  fmtFraccion,
  galeriaEvidenciasPdf,
  GRUPOS_EVIDENCIA,
  urlEvidencia,
} from "./exportCalidad";
import {
  ChartCard,
  COLOR_RESULTADO,
  colorPorcentaje,
  EstadoVacio,
  isMobile,
  KpiRow,
  NIVO_THEME,
  RESULTADO_LABEL,
  RESULTADO_TAG,
  tagPorcentaje,
  TooltipCalidad,
} from "./reporteCalidadStyles";

const PERIODO_INICIAL = { periodo: [moment().subtract(1, "month"), moment()] };

const recortar = (texto, largo) =>
  texto && texto.length > largo ? `${texto.substring(0, largo)}…` : texto;

// El color va por INCUMPLIMIENTO: más alto = peor
const colorIncumplimiento = (p) => colorPorcentaje(100 - p);
const tagIncumplimiento = (p) => tagPorcentaje(100 - p);

// Placeholder de miniatura cuando la evidencia no se puede cargar (en desarrollo
// las imágenes se sirven desde el backend, no desde el host de CRA).
const FALLBACK_EVIDENCIA =
  "data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxMDgiIGhlaWdodD0iODQiPjxyZWN0IHdpZHRoPSIxMDgiIGhlaWdodD0iODQiIGZpbGw9IiNmMWY1ZjkiLz48dGV4dCB4PSI1NCIgeT0iNDYiIGZvbnQtc2l6ZT0iMTAiIGZpbGw9IiM5NGEzYjgiIHRleHQtYW5jaG9yPSJtaWRkbGUiPnNpbiBpbWFnZW48L3RleHQ+PC9zdmc+";

// Cuenta las evidencias de un punto separadas por categoría.
const contarEvidencias = (evidencias = []) =>
  GRUPOS_EVIDENCIA.map(({ categoria }) => ({
    categoria,
    total: evidencias.filter((e) => categoriaEvidencia(e) === categoria).length,
  }));

/**
 * Reporte de un proceso específico, con el desglose punto de control → criterios de aceptación
 * y el % de incumplimiento de cada nivel. Es el único reporte que adjunta las evidencias
 * fotográficas de los registros rechazados.
 */
class ReporteProceso extends Component {
  formRef = React.createRef();
  tableRef = null;

  state = {
    loading: false,
    registros: [],
    totalRegistros: 0,
    resumen: null,
    proceso: null,
    porPunto: [],
    porColaborador: [],
    totalEvidencias: 0,
    empresa: null,
    currentFilters: {},
  };

  buildPayload(values = {}) {
    const payload = {};
    if (values.programaId) payload.programaId = values.programaId;
    if (values.procesoId) payload.procesoId = values.procesoId;
    if (values.ejecutadoPor) payload.ejecutadoPor = values.ejecutadoPor;
    if (values.periodo?.length === 2) {
      payload.fechaInicio = values.periodo[0].startOf("day").toISOString();
      payload.fechaFin = values.periodo[1].endOf("day").toISOString();
    }
    return payload;
  }

  onFetchData(params) {
    // El reporte no tiene sentido sin proceso: se espera a que el usuario elija uno
    if (!params.procesoId) return;

    this.setState({ loading: true });
    axios
      .post(
        "programa/reporteProceso",
        { empresaId: this.props.empresaId, ...params },
        { headers: this.props.headersToken }
      )
      .then((res) => {
        if (res.data.success === 2) {
          this.setState({
            registros: res.data.data,
            totalRegistros: res.data.totalRecords,
            resumen: res.data.resumen,
            proceso: res.data.proceso,
            porPunto: res.data.porPunto ?? [],
            porColaborador: res.data.porColaborador ?? [],
            totalEvidencias: res.data.totalEvidencias ?? 0,
            empresa: res.data.empresa ?? null,
          });
        } else {
          message.error(res.data.message ?? "No se pudo generar el reporte");
        }
      })
      .catch(() => message.error("Error de conexión"))
      .finally(() => this.setState({ loading: false }));
  }

  consultarReporte(values) {
    const payload = this.buildPayload(values);
    this.setState({ currentFilters: payload });
    this.tableRef?.updateExternalFilters(payload);
  }

  /** `incluirEvidencias` solo lo pide el PDF: leer las fotos de disco es costoso. */
  async fetchAll(incluirEvidencias = false) {
    const res = await axios.post(
      "programa/reporteProceso",
      {
        empresaId: this.props.empresaId,
        ...this.state.currentFilters,
        ...(incluirEvidencias ? { incluirEvidencias: true } : {}),
      },
      { headers: this.props.headersToken }
    );
    if (res.data.success !== 2) throw new Error("Error al obtener datos");
    return res.data;
  }

  /**
   * Excel jerárquico en dos columnas "en escalera": el punto ocupa la primera y sus criterios
   * la segunda, así la jerarquía se ve sola y no hace falta una columna de tipo/nivel.
   * El foco es "% No cumple", que es la métrica que se quiere leer de un vistazo.
   */
  async descargarExcel() {
    this.setState({ loading: true });
    try {
      const { porPunto, proceso, resumen } = await this.fetchAll();

      if (porPunto.length === 0) {
        message.warning("No hay puntos de control evaluados en el período");
        return;
      }

      const filas = [];
      porPunto.forEach((p) => {
        filas.push({
          "Punto de control": p.nombre,
          "Criterio de aceptación": "",
          Evaluaciones: p.evaluaciones,
          Cumplidos: p.completados,
          "No cumplidos": p.noCompletados,
          "% Cumple": `${p.porcentajeCumplimiento}%`,
          "% No cumple": `${p.porcentajeIncumplimiento}%`,
          "Última evaluación": fmtFecha(p.ultimaEvaluacion),
          Observaciones: "",
          "Evidencias hallazgo": p.evidenciasRechazo
            .filter((e) => categoriaEvidencia(e) === "hallazgo")
            .map((e) => urlEvidencia(e.url))
            .join("  |  "),
          "Evidencias resolución": p.evidenciasRechazo
            .filter((e) => categoriaEvidencia(e) === "resolucion")
            .map((e) => urlEvidencia(e.url))
            .join("  |  "),
        });

        p.criterios.forEach((c) => {
          filas.push({
            "Punto de control": "",
            "Criterio de aceptación": c.criterio,
            Evaluaciones: c.evaluaciones,
            Cumplidos: c.cumplidos,
            "No cumplidos": c.noCumplidos,
            "% Cumple": `${c.porcentajeCumplimiento}%`,
            "% No cumple": `${c.porcentajeIncumplimiento}%`,
            "Última evaluación": "",
            Observaciones: c.observaciones.map((o) => o.observacion).join(" | "),
            "Evidencias hallazgo": "",
            "Evidencias resolución": "",
          });
        });

        // Renglón en blanco para separar visualmente cada bloque punto + criterios
        filas.push({});
      });

      // Totales del proceso al final
      filas.push({
        "Punto de control": `TOTAL — ${proceso?.nombre ?? ""}`,
        "Criterio de aceptación": "",
        Evaluaciones: resumen?.total ?? 0,
        Cumplidos: resumen?.completados ?? 0,
        "No cumplidos": resumen?.rechazados ?? 0,
        "% Cumple": `${resumen?.porcentajeCumplimiento ?? 0}%`,
        "% No cumple": `${100 - (resumen?.porcentajeCumplimiento ?? 0)}%`,
        "Última evaluación": "",
        Observaciones: "",
        "Evidencias hallazgo": "",
        "Evidencias resolución": "",
      });

      exportarExcel("Reporte_Proceso_Calidad", "Puntos y Criterios", filas);
    } catch {
      message.error("No se pudo generar el Excel");
    } finally {
      this.setState({ loading: false });
    }
  }

  async descargarPdf() {
    this.setState({ loading: true });
    try {
      const {
        data: registros,
        resumen,
        empresa,
        proceso,
        porPunto,
        porColaborador,
      } = await this.fetchAll(true);
      const { usaPasos, usaCriterios } = resumen ?? {};

      // Tabla jerárquica: el punto en negritas y sus criterios indentados debajo
      const filasJerarquia = porPunto.flatMap((p) => [
        [
          { text: p.nombre, bold: true },
          { text: `${p.evaluaciones}`, alignment: "center", bold: true },
          { text: `${p.completados}`, alignment: "center", bold: true },
          { text: `${p.noCompletados}`, alignment: "center", bold: true },
          { text: `${p.porcentajeCumplimiento}%`, alignment: "center", bold: true },
          { text: `${p.porcentajeIncumplimiento}%`, alignment: "center", bold: true },
        ],
        ...p.criterios.map((c) => [
          { text: `      - ${c.criterio}`, fontSize: 7 },
          { text: `${c.evaluaciones}`, alignment: "center", fontSize: 7 },
          { text: `${c.cumplidos}`, alignment: "center", fontSize: 7 },
          { text: `${c.noCumplidos}`, alignment: "center", fontSize: 7 },
          { text: `${c.porcentajeCumplimiento}%`, alignment: "center", fontSize: 7 },
          { text: `${c.porcentajeIncumplimiento}%`, alignment: "center", fontSize: 7 },
        ]),
      ]);

      descargarPdfCalidad({
        empresa,
        titulo: "Reporte por Proceso",
        subtitulo: proceso?.nombre ?? "-",
        extras: [
          `Programa: ${proceso?.programa ?? "-"}`,
          `Puntos de control: ${porPunto.length}`,
          `Total registros: ${registros.length}`,
        ],
        nombreArchivo: "Reporte_Proceso_Calidad",
        kpis: [
          { label: "Registros", value: resumen?.total ?? 0 },
          { label: "Completados", value: resumen?.completados ?? 0 },
          { label: "Con obs.", value: resumen?.conObservaciones ?? 0 },
          { label: "Rechazados", value: resumen?.rechazados ?? 0 },
          { label: "% Cumplimiento", value: `${resumen?.porcentajeCumplimiento ?? 0}%` },
          usaCriterios && { label: "% Criterios", value: `${resumen?.porcentajeCriterios ?? 0}%` },
          usaPasos && { label: "% Pasos", value: `${resumen?.porcentajePasos ?? 0}%` },
          {
            label: "Dur. promedio",
            value:
              resumen?.duracionPromedioMinutos != null
                ? `${resumen.duracionPromedioMinutos} min`
                : "-",
          },
        ],
        secciones: [
          {
            titulo: "Cumplimiento por punto de control y criterio",
            columnas: [
              "Punto de control / Criterio",
              "Evaluaciones",
              "Cumplidos",
              "No cumplidos",
              "% Cumple",
              "% No cumple",
            ],
            widths: ["*", 62, 56, 66, 56, 66],
            filas: filasJerarquia,
            vacio: "No se evaluó ningún punto de control en el período.",
          },
          {
            titulo: `Desglose por colaborador (${porColaborador.length})`,
            columnas: [
              "Colaborador",
              "Registros",
              "Compl.",
              "Con obs.",
              "Rechaz.",
              "% Cumpl.",
              "Dur. prom.",
            ],
            widths: ["*", 56, 46, 52, 50, 56, 58],
            filas: porColaborador.map((g) => [
              g.nombre,
              { text: `${g.total}`, alignment: "center" },
              { text: `${g.completados}`, alignment: "center" },
              { text: `${g.conObservaciones}`, alignment: "center" },
              { text: `${g.rechazados}`, alignment: "center" },
              { text: `${g.porcentajeCumplimiento}%`, alignment: "center", bold: true },
              { text: fmtDuracion(g.duracionPromedioMinutos), alignment: "center" },
            ]),
            vacio: "Ningún registro tiene colaborador identificado.",
          },
        ],
        postSecciones: galeriaEvidenciasPdf("Evidencias de registros rechazados", porPunto),
      });
    } catch {
      message.error("No se pudo generar el PDF");
    } finally {
      this.setState({ loading: false });
    }
  }

  kpis() {
    const { resumen, porPunto, totalEvidencias } = this.state;
    if (!resumen) return [];

    const peor = porPunto[0];
    return [
      { key: "total", label: "Registros", value: resumen.total, color: "#0f172a" },
      { key: "puntos", label: "Puntos evaluados", value: porPunto.length, color: "#00BFD6" },
      {
        key: "cumplimiento",
        label: "% Cumplimiento",
        value: `${resumen.porcentajeCumplimiento}%`,
        color: colorPorcentaje(resumen.porcentajeCumplimiento),
      },
      resumen.usaCriterios && {
        key: "criterios",
        label: "% Criterios",
        value: `${resumen.porcentajeCriterios}%`,
        color: colorPorcentaje(resumen.porcentajeCriterios),
        hint: `${resumen.totalCriteriosCumplidos}/${resumen.totalCriterios}`,
      },
      peor && {
        key: "peor",
        label: "Punto que más falla",
        value: `${peor.porcentajeIncumplimiento}%`,
        color: colorIncumplimiento(peor.porcentajeIncumplimiento),
        hint: recortar(peor.nombre, 22),
      },
      { key: "rechazados", label: "Rechazados", value: resumen.rechazados, color: COLOR_RESULTADO.rechazado },
      {
        key: "evidencias",
        label: "Evidencias de rechazo",
        value: totalEvidencias,
        color: "#8b5cf6",
        hint: "en el PDF",
      },
    ].filter(Boolean);
  }

  ficha() {
    const { proceso, porPunto } = this.state;
    if (!proceso) return null;

    const principal = proceso.responsables?.find((r) => r.esPrincipal);
    const datos = [
      { label: "Programa", valor: proceso.programa },
      { label: "Responsable principal", valor: principal?.nombre ?? "-" },
      { label: "Prioridad", valor: proceso.prioridad != null ? `${proceso.prioridad}` : "-" },
      { label: "Estado", valor: proceso.estado === 1 ? "Activo" : "Deshabilitado" },
      {
        label: "Puntos de control",
        valor: `${porPunto.length} evaluados de ${proceso.totalPuntosCatalogo ?? 0}`,
      },
      {
        label: "Plantilla",
        valor: proceso.esPlantilla ? `Sí (v${proceso.version ?? "-"})` : "No",
      },
    ];

    return (
      <div className="cal-ficha">
        <div className="cal-ficha-nombre">{proceso.nombre}</div>
        <Row gutter={[16, 8]}>
          {datos.map((d) => (
            <Col key={d.label} xs={24} sm={12} md={8}>
              <div className="cal-ficha-dato">
                {d.label}: <b>{d.valor || "-"}</b>
              </div>
            </Col>
          ))}
        </Row>
      </div>
    );
  }

  grafica() {
    const { porPunto } = this.state;
    if (porPunto.length === 0) return null;

    const movil = isMobile();
    // Los que más incumplen arriba (nivo pinta el primer item abajo)
    const data = [...porPunto]
      .slice(0, 12)
      .reverse()
      .map((p) => ({
        punto: recortar(p.nombre, movil ? 16 : 32),
        puntoCompleto: p.nombre,
        evaluaciones: p.evaluaciones,
        noCompletados: p.noCompletados,
        incumplimiento: p.porcentajeIncumplimiento,
      }));

    return (
      <Row style={{ marginBottom: 14 }}>
        <Col span={24}>
          <ChartCard
            label="Prioridad"
            titulo="% de incumplimiento por punto de control"
            pill={`${porPunto.length} puntos`}
            accent="linear-gradient(90deg, #ef4444, #f59e0b)"
            delay={0.06}
            altura={Math.max(230, data.length * 30 + 60)}
          >
            <ResponsiveBar
              data={data}
              keys={["incumplimiento"]}
              indexBy="punto"
              layout="horizontal"
              margin={{ top: 8, right: 44, bottom: 40, left: movil ? 118 : 210 }}
              padding={0.3}
              maxValue={100}
              colors={({ data: d }) => colorIncumplimiento(d.incumplimiento)}
              borderRadius={4}
              axisTop={null}
              axisRight={null}
              axisBottom={{ tickSize: 0, tickPadding: 8, format: (v) => `${v}%` }}
              axisLeft={{ tickSize: 0, tickPadding: 8 }}
              enableGridY={false}
              gridXValues={[0, 25, 50, 75, 100]}
              label={(d) => `${d.value}%`}
              labelSkipWidth={30}
              labelTextColor="#ffffff"
              theme={NIVO_THEME}
              tooltip={({ data: d }) => (
                <TooltipCalidad
                  color={colorIncumplimiento(d.incumplimiento)}
                  titulo={d.puntoCompleto}
                  filas={[
                    { label: "Evaluaciones", value: d.evaluaciones },
                    { label: "No completados", value: d.noCompletados },
                    { label: "% No cumple", value: `${d.incumplimiento}%` },
                  ]}
                />
              )}
            />
          </ChartCard>
        </Col>
      </Row>
    );
  }

  /** Tabla jerárquica: cada punto se expande a sus criterios de aceptación. */
  tablaJerarquia() {
    const { porPunto, resumen } = this.state;
    if (porPunto.length === 0) return null;

    const columnasPunto = [
      { title: "Punto de control", dataIndex: "nombre", ellipsis: true },
      { title: "Criterios", key: "nCrit", align: "center", width: 90, render: (_, p) => p.criterios.length },
      { title: "Evaluaciones", dataIndex: "evaluaciones", align: "center", width: 110 },
      { title: "Cumplidos", dataIndex: "completados", align: "center", width: 100 },
      {
        title: "No cumplidos",
        dataIndex: "noCompletados",
        align: "center",
        width: 115,
        render: (v) => (v > 0 ? <span style={{ color: "#ef4444", fontWeight: 600 }}>{v}</span> : v),
      },
      {
        title: "% No cumple",
        dataIndex: "porcentajeIncumplimiento",
        align: "center",
        width: 115,
        render: (v) => <Tag color={tagIncumplimiento(v)}>{v}%</Tag>,
      },
      resumen?.usaPasos && {
        title: "% Pasos",
        dataIndex: "porcentajePasos",
        align: "center",
        width: 95,
        render: (v) => `${v}%`,
      },
      {
        title: "Evidencias",
        key: "evidencias",
        align: "center",
        width: 120,
        render: (_, p) => {
          if (p.evidenciasRechazo.length === 0) return "-";
          // Un solo número sería ambiguo ahora que un punto puede traer el antes
          // y el después: se muestra el desglose por categoría.
          return (
            <Space size={4}>
              {GRUPOS_EVIDENCIA.map(({ categoria, titulo }, i) => {
                const total = contarEvidencias(p.evidenciasRechazo)[i].total;
                return total > 0 ? (
                  <Tag key={categoria} color={categoria === "hallazgo" ? "red" : "green"} title={titulo}>
                    {total}
                  </Tag>
                ) : null;
              })}
            </Space>
          );
        },
      },
    ].filter(Boolean);

    return (
      <div className="cal-card" style={{ marginBottom: 14, animationDelay: "0.12s" }}>
        <div className="cal-card-head">
          <div>
            <div className="cal-card-label">Detalle</div>
            <h2 className="cal-card-title">Puntos de control y sus criterios</h2>
          </div>
          <span className="cal-pill">
            <span className="cal-pill-dot" />
            expande un punto
          </span>
        </div>
        <div className="cal-card-accent" />
        {/*
          El scroll horizontal va en este wrapper y NO en la prop `scroll` de antd: con
          scroll={{x}} la tabla externa colapsa el contenido de expandedRowRender.
        */}
        <div style={{ overflowX: "auto" }}>
          <Table
            size="small"
            rowKey="puntoId"
            dataSource={porPunto}
            columns={columnasPunto}
            pagination={false}
            expandable={{
              expandedRowRender: (p) => (
                <div style={{ padding: "6px 0 8px 8px" }}>
                  <div className="cal-sub-titulo">
                    Criterios de aceptación ({p.criterios.length})
                  </div>

                  {p.criterios.length === 0 ? (
                    <div style={{ fontSize: 12, color: "#94a3b8", marginBottom: 8 }}>
                      Este punto no tiene criterios de aceptación capturados.
                    </div>
                  ) : (
                    <div className="cal-grid-criterios">
                      <div className="cal-grid-head">Criterio</div>
                      <div className="cal-grid-head cal-c">Eval.</div>
                      <div className="cal-grid-head cal-c">Cumpl.</div>
                      <div className="cal-grid-head cal-c">No cumpl.</div>
                      <div className="cal-grid-head cal-c">% No cumple</div>
                      <div className="cal-grid-head">Observaciones</div>

                      {p.criterios.map((c) => (
                        <React.Fragment key={c.criterio}>
                          <div className="cal-grid-cell">{c.criterio}</div>
                          <div className="cal-grid-cell cal-c">{c.evaluaciones}</div>
                          <div className="cal-grid-cell cal-c">{c.cumplidos}</div>
                          <div className="cal-grid-cell cal-c">
                            {c.noCumplidos > 0 ? (
                              <span style={{ color: "#ef4444", fontWeight: 700 }}>
                                {c.noCumplidos}
                              </span>
                            ) : (
                              c.noCumplidos
                            )}
                          </div>
                          <div className="cal-grid-cell cal-c">
                            <Tag color={tagIncumplimiento(c.porcentajeIncumplimiento)}>
                              {c.porcentajeIncumplimiento}%
                            </Tag>
                          </div>
                          <div className="cal-grid-cell" style={{ color: "#64748b" }}>
                            {c.observaciones?.length > 0
                              ? c.observaciones.map((o) => o.observacion).join(" · ")
                              : "—"}
                          </div>
                        </React.Fragment>
                      ))}
                    </div>
                  )}

                  {p.evidenciasRechazo.length > 0 && (
                    <div style={{ marginTop: 14 }}>
                      <div className="cal-sub-titulo">
                        Evidencias de registros rechazados ({p.evidenciasRechazo.length})
                      </div>
                      {GRUPOS_EVIDENCIA.map(({ categoria, titulo, color }) => {
                        const grupo = p.evidenciasRechazo.filter(
                          (e) => categoriaEvidencia(e) === categoria
                        );
                        if (grupo.length === 0) return null;

                        return (
                          <div
                            key={categoria}
                            style={{ marginTop: 8, paddingLeft: 8, borderLeft: `3px solid ${color}` }}
                          >
                            <div style={{ fontSize: 11, color, marginBottom: 4 }}>
                              {titulo} ({grupo.length})
                            </div>
                            <Space size={8} wrap>
                              {grupo.map((e, i) => (
                                <div key={i} style={{ width: 108 }}>
                                  <Image
                                    src={urlEvidencia(e.url)}
                                    width={108}
                                    height={84}
                                    style={{ objectFit: "cover", borderRadius: 8 }}
                                    fallback={FALLBACK_EVIDENCIA}
                                  />
                                  <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 2 }}>
                                    {e.registroNumero ? `#${e.registroNumero} · ` : ""}
                                    {e.fecha ? moment(e.fecha).format("DD/MM/YY") : ""}
                                  </div>
                                </div>
                              ))}
                            </Space>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ),
            }}
          />
        </div>
      </div>
    );
  }

  columnasRegistros() {
    const { resumen } = this.state;
    return [
      { title: "Ejecutado por", key: "ejecutadoPor", render: (_, r) => fmtEjecutado(r), ellipsis: true },
      {
        title: "Resultado",
        dataIndex: "resultado",
        width: 140,
        render: (v) => <Tag color={RESULTADO_TAG[v] ?? "default"}>{RESULTADO_LABEL[v] ?? v}</Tag>,
      },
      { title: "Inicio", dataIndex: "fechaInicio", render: fmtFecha, width: 120 },
      { title: "Fin", dataIndex: "fechaFin", render: fmtFecha, width: 120 },
      {
        title: "Duración",
        dataIndex: "duracionMinutos",
        render: fmtDuracion,
        align: "center",
        width: 90,
      },
      resumen?.usaPuntos && {
        title: "Puntos",
        key: "puntos",
        align: "center",
        width: 85,
        render: (_, r) => fmtFraccion(r.puntosCompletados, r.puntosTotales),
      },
      resumen?.usaCriterios && {
        title: "Criterios ✓",
        key: "criterios",
        align: "center",
        width: 100,
        render: (_, r) => fmtFraccion(r.criteriosCumplidos, r.criteriosTotales),
      },
      {
        title: "% Cumpl.",
        dataIndex: "porcentajeCumplimiento",
        align: "center",
        width: 95,
        render: (v) => (v != null ? <Tag color={tagPorcentaje(v)}>{v}%</Tag> : "-"),
      },
      {
        title: "Observaciones",
        dataIndex: "observaciones",
        ellipsis: true,
        render: (v) => v || "-",
      },
    ].filter(Boolean);
  }

  render() {
    const { registros, totalRegistros, resumen, loading, currentFilters, porPunto } = this.state;
    const sinProceso = !currentFilters.procesoId;
    const sinDatos = resumen && resumen.total === 0;

    return (
      <Form
        ref={this.formRef}
        name="reporteCalidadProceso"
        onFinish={this.consultarReporte.bind(this)}
        onFinishFailed={ErrorItem}
        initialValues={PERIODO_INICIAL}
      >
        <Spin spinning={loading} wrapperClassName="cal-spin">
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
            <Space>
              {resumen && !sinProceso && (
                <ButtonItem verde onClick={() => this.descargarExcel()} icon={<FileExcelOutlined />}>
                  Excel
                </ButtonItem>
              )}
              {resumen && !sinProceso && (
                <ButtonItem rojo onClick={() => this.descargarPdf()} icon={<FilePdfOutlined />}>
                  PDF
                </ButtonItem>
              )}
              <ButtonItem type="primary" htmlType="submit">
                Consultar
              </ButtonItem>
            </Space>
          </div>

          <FiltrosCalidad
            campos={["periodo", "programa", "proceso", "colaborador"]}
            requeridos={["programaId", "procesoId"]}
            formRef={this.formRef}
          />

          {sinProceso && (
            <EstadoVacio
              titulo="Elige un proceso"
              texto="Selecciona un programa y luego uno de sus procesos para ver el cumplimiento de cada punto de control y de cada criterio de aceptación."
            />
          )}

          {!sinProceso && (
            <>
              {this.ficha()}
              <KpiRow items={this.kpis()} />
              {this.grafica()}
              {this.tablaJerarquia()}
              {sinDatos && (
                <EstadoVacio
                  titulo="Sin registros en el período"
                  texto="Este proceso no se ejecutó en el rango de fechas seleccionado."
                />
              )}
              {!sinDatos && porPunto.length === 0 && (
                <Empty
                  description="Hay registros pero ningún punto de control capturado"
                  style={{ marginBottom: 14 }}
                />
              )}
            </>
          )}

          {/*
            La tabla se monta siempre: TableItemDinamico expone el ref con el que
            updateExternalFilters dispara la consulta, así que desmontarla rompería el filtro.
          */}
          <div style={{ display: sinProceso || sinDatos ? "none" : "block" }}>
            <TableItemDinamico
              onRef={(ref) => (this.tableRef = ref)}
              totalRecords={totalRegistros}
              onFetchData={this.onFetchData.bind(this)}
              loading={loading}
              size="small"
              dataSource={registros}
              columns={this.columnasRegistros()}
              rowKey={(r) => r._id?.toString()}
            />
          </div>
        </Spin>
      </Form>
    );
  }
}

const mapStateToProps = (state) => ({
  headersToken: state.user.headersToken,
  empresaId: state.empresa.id,
});

export default connect(mapStateToProps)(ReporteProceso);
